/**
 * Termsheet Data Point Registry
 *
 * Canonical registry of data points for termsheet templates
 * (Template.kind === 'TERMSHEET').
 *
 * Surfaces (mirrors PARTNERSHIP_DATA_POINTS):
 * - Designer ContextRail filters this list by `category` so admins can drag
 *   typed canonical keys into the placed PDF text fields.
 * - Envelope Generate-time resolution — `termsheet-canonical-key.resolver.ts`
 *   maps these keys to live values from CRM/Case/Tenant before sending to
 *   DocuSeal as native field prefill (`submitters[i].fields[].default_value`).
 *
 * Multi-tenancy: this registry is platform-scoped (no businessId). Tenants
 * override per-template field bindings; the canonical-key vocabulary is
 * shared.
 *
 * Sensitive-key invariant (CLAUDE.md §2 + #1531 v4 §6.2): SSN, DOB, and
 * personal credit-score fields are intentionally absent from this registry.
 * Termsheets are pre-approval offers — they reference borrower entity name,
 * loan terms, and property address, not personal-identifying data beyond
 * what's already on the contract.
 *
 * @see #1531 follow-up — eliminate cloneTemplateWithDocument backdoor
 *      from term-sheet-generation.service by routing dynamic values
 *      through DocuSeal native prefill instead of runtime PDF replacement.
 */

export interface TermSheetDataPoint {
  /** Unique canonical key used across designer, mapping rows, and envelope generation. */
  canonicalKey: string;
  /** Human-readable display name shown in the designer's data-point picker. */
  label: string;
  /** Tooltip / help text describing what the value represents. */
  description: string;
  /** Logical grouping for ContextRail accordion sections. */
  category: 'borrower' | 'property' | 'loan_terms' | 'lender' | 'dates';
  /** Expected value type for runtime coercion at envelope-Generate time. */
  type: 'string' | 'number' | 'date' | 'boolean';
  /** Whether the field MUST be resolved before the envelope can be sent. */
  required: boolean;
}

export const TERMSHEET_DATA_POINTS: readonly TermSheetDataPoint[] = [
  // --------------------------------------------------------------------------
  // BORROWER side
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'borrower_legal_name',
    label: 'Borrower Legal Name',
    description: 'Principal borrower legal name (individual or entity).',
    category: 'borrower',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'borrower_entity_name',
    label: 'Borrower Entity Name',
    description: 'Entity (LLC/Corp) borrowing — empty when borrower is individual.',
    category: 'borrower',
    type: 'string',
    required: false,
  },
  {
    canonicalKey: 'borrower_email',
    label: 'Borrower Email',
    description: 'Primary borrower contact email.',
    category: 'borrower',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'borrower_phone',
    label: 'Borrower Phone',
    description: 'Primary borrower contact phone.',
    category: 'borrower',
    type: 'string',
    required: false,
  },

  // --------------------------------------------------------------------------
  // PROPERTY side
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'property_address',
    label: 'Property Address',
    description: 'Single-line property address (street, city, state, ZIP).',
    category: 'property',
    type: 'string',
    required: true,
  },
  {
    canonicalKey: 'property_type',
    label: 'Property Type',
    description: 'Property classification (e.g. SFR, multifamily, mixed-use).',
    category: 'property',
    type: 'string',
    required: false,
  },

  // --------------------------------------------------------------------------
  // LOAN TERMS
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'loan_amount',
    label: 'Loan Amount',
    description: 'Principal loan amount, formatted as currency.',
    category: 'loan_terms',
    type: 'number',
    required: true,
  },
  {
    canonicalKey: 'interest_rate',
    label: 'Interest Rate',
    description: 'Annual interest rate, formatted as percent (e.g. 8.50%).',
    category: 'loan_terms',
    type: 'number',
    required: true,
  },
  {
    canonicalKey: 'loan_term_months',
    label: 'Loan Term (months)',
    description: 'Loan term in months (e.g. "12 months", "24 months").',
    category: 'loan_terms',
    type: 'number',
    required: true,
  },
  {
    canonicalKey: 'origination_points',
    label: 'Origination Points',
    description: 'Origination points expressed as a percentage of principal.',
    category: 'loan_terms',
    type: 'number',
    required: false,
  },
  {
    canonicalKey: 'loan_to_value',
    label: 'Loan-to-Value (LTV)',
    description: 'LTV ratio as a percentage.',
    category: 'loan_terms',
    type: 'number',
    required: false,
  },
  {
    canonicalKey: 'loan_type',
    label: 'Loan Type',
    description: 'Bridge / Fix-and-Flip / DSCR / Construction / Ground-up.',
    category: 'loan_terms',
    type: 'string',
    required: false,
  },

  // --------------------------------------------------------------------------
  // LENDER side
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'lender_legal_name',
    label: 'Lender Legal Name',
    description: 'Legal name of the lender business.',
    category: 'lender',
    type: 'string',
    required: true,
  },

  // --------------------------------------------------------------------------
  // DATES
  // --------------------------------------------------------------------------
  {
    canonicalKey: 'issue_date',
    label: 'Issue Date',
    description: 'Date the termsheet is issued.',
    category: 'dates',
    type: 'date',
    required: true,
  },
  {
    canonicalKey: 'expiration_date',
    label: 'Expiration Date',
    description: 'Date the termsheet offer expires.',
    category: 'dates',
    type: 'date',
    required: true,
  },
];

/** Flat list of every canonical key in this registry — convenience for callers that just need string lookup. */
export const TERMSHEET_CANONICAL_KEYS: readonly string[] = TERMSHEET_DATA_POINTS.map((p) => p.canonicalKey);
